/**
 * Typed wrappers over the REST surface. Keeping them in one file means the
 * component layer never builds a URL by hand.
 */

import type {
  BoardPayload,
  BoardSummary,
  Card,
  CardDetail,
  ChecklistItem,
  Column,
  Comment,
  Group,
  Label,
  Me,
  UserSummary,
} from '@rm/shared';
import { api } from './client.js';

export const authApi = {
  me: () => api.get<Me>('/api/me'),
  login: (email: string, password: string) =>
    api.post<Me>('/api/auth/login', { email, password }),
  logout: () => api.post<void>('/api/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<void>('/api/me/password', { currentPassword, newPassword }),
  updateProfile: (input: { firstName: string; lastName: string }) =>
    api.patch<Me>('/api/me/profile', input),
  invite: (token: string) => api.get<{ email: string; name: string }>(`/api/auth/invite/${token}`),
  acceptInvite: (token: string, name: string, password: string) =>
    api.post<Me>(`/api/auth/invite/${token}/accept`, { name, password }),
};

export const boardApi = {
  list: () => api.get<BoardSummary[]>('/api/boards'),
  get: (boardId: string) => api.get<BoardPayload>(`/api/boards/${boardId}`),
  cards: (boardId: string) => api.get<Card[]>(`/api/boards/${boardId}/cards`),
  create: (name: string) => api.post<BoardPayload>('/api/boards', { name }),
  update: (boardId: string, patch: { name?: string; slug?: string }) =>
    api.patch<BoardSummary>(`/api/boards/${boardId}`, patch),
  remove: (boardId: string) => api.delete<void>(`/api/boards/${boardId}`),
  setColumnWidths: (widths: Array<{ columnId: string; widthPx: number }>) =>
    api.put<void>('/api/me/column-widths', { widths }),
};

export const columnApi = {
  create: (boardId: string, input: { name: string; afterColumnId?: string | null }) =>
    api.post<Column>(`/api/boards/${boardId}/columns`, input),
  update: (
    columnId: string,
    patch: {
      name?: string;
      wipLimit?: number | null;
      color?: string | null;
      countsAsDone?: boolean;
    },
  ) => api.patch<Column>(`/api/columns/${columnId}`, patch),
  move: (columnId: string, input: { afterColumnId?: string | null; beforeColumnId?: string | null }) =>
    api.post<Column>(`/api/columns/${columnId}/move`, input),
  /** Empties this column into another, keeping the cards' relative order. */
  moveCards: (columnId: string, toColumnId: string) =>
    api.post<{ moved: number }>(`/api/columns/${columnId}/move-cards`, { toColumnId }),
  remove: (columnId: string) => api.delete<void>(`/api/columns/${columnId}`),
};

export interface CreateCardInput {
  columnId: string;
  title: string;
  summary?: string;
  labelIds?: string[];
  groupId?: string | null;
  assigneeId?: string | null;
  dueAt?: string | null;
  afterCardId?: string | null;
  beforeCardId?: string | null;
}

export interface MoveCardInput {
  toColumnId: string;
  afterCardId?: string | null;
  beforeCardId?: string | null;
}

export const cardApi = {
  get: (cardId: string) => api.get<CardDetail>(`/api/cards/${cardId}`),
  create: (input: CreateCardInput) => api.post<Card>('/api/cards', input),
  update: (
    cardId: string,
    patch: {
      title?: string;
      summary?: string;
      details?: unknown;
      assigneeId?: string | null;
      groupId?: string | null;
      dueAt?: string | null;
      expectedUpdatedAt?: string;
    },
  ) => api.patch<Card>(`/api/cards/${cardId}`, patch),
  move: (cardId: string, input: MoveCardInput) =>
    api.post<Card>(`/api/cards/${cardId}/move`, input),
  setLabels: (cardId: string, labelIds: string[]) =>
    api.put<Card>(`/api/cards/${cardId}/labels`, { labelIds }),
  archive: (cardId: string) => api.post<void>(`/api/cards/${cardId}/archive`),
  remove: (cardId: string) => api.delete<void>(`/api/cards/${cardId}`),
};

export const checklistApi = {
  create: (cardId: string, body: string) =>
    api.post<ChecklistItem>(`/api/cards/${cardId}/checklist`, { body }),
  update: (itemId: string, patch: { body?: string; isDone?: boolean }) =>
    api.patch<ChecklistItem>(`/api/checklist/${itemId}`, patch),
  remove: (itemId: string) => api.delete<void>(`/api/checklist/${itemId}`),
};

export const commentApi = {
  list: (cardId: string) => api.get<Comment[]>(`/api/cards/${cardId}/comments`),
  create: (cardId: string, body: unknown) =>
    api.post<Comment>(`/api/cards/${cardId}/comments`, { body }),
  remove: (commentId: string) => api.delete<void>(`/api/comments/${commentId}`),
};

export const labelApi = {
  create: (boardId: string, input: { name: string; color: string }) =>
    api.post<Label>(`/api/boards/${boardId}/labels`, input),
  update: (labelId: string, patch: { name?: string; color?: string }) =>
    api.patch<Label>(`/api/labels/${labelId}`, patch),
  remove: (labelId: string) => api.delete<void>(`/api/labels/${labelId}`),
};

export const groupApi = {
  create: (boardId: string, name: string) =>
    api.post<Group>(`/api/boards/${boardId}/groups`, { name }),
  update: (groupId: string, name: string) => api.patch<Group>(`/api/groups/${groupId}`, { name }),
  remove: (groupId: string) => api.delete<void>(`/api/groups/${groupId}`),
};

export const userApi = {
  list: () => api.get<UserSummary[]>('/api/users'),
  invite: (input: { email: string; name: string; role: string }) =>
    api.post<{ user: UserSummary; inviteUrl: string; expiresAt: string }>(
      '/api/users/invite',
      input,
    ),
  update: (userId: string, patch: { name?: string; role?: string; isActive?: boolean }) =>
    api.patch<UserSummary>(`/api/users/${userId}`, patch),
  deactivate: (userId: string) => api.delete<void>(`/api/users/${userId}`),
};
